import { ChatReferenceImage } from "@indigo-components/components/Chat/ChatReferenceImage/ChatReferenceImage";
import { errorReporter } from "~/error-handling";
import type { ReferenceImage } from "~/pydantic_models/services/models/chat_blocks";
import type { ChatBlockRendererProps } from "./types";
import { isHttpsUrl, usePresignedReadUrls } from "./usePresignedReadUrls";

export function parseReferenceImages(
  data: Record<string, unknown> | null | undefined,
): ReferenceImage[] | null {
  if (
    !Array.isArray(data?.images) ||
    !data.images.length ||
    data.images.length > 3
  )
    return null;
  const images: ReferenceImage[] = [];
  for (const raw of data.images) {
    if (!raw || typeof raw !== "object") return null;
    const image = raw as Partial<ReferenceImage>;
    if (
      typeof image.s3_key !== "string" ||
      !image.s3_key.startsWith("chat-attachments/") ||
      typeof image.s3_bucket !== "string" ||
      !isHttpsUrl(image.source_url) ||
      typeof image.source_title !== "string" ||
      typeof image.caption !== "string" ||
      typeof image.alt_text !== "string"
    )
      return null;
    images.push(image as ReferenceImage);
  }
  return images;
}

const ACCESS_ERROR = {
  message: "Reference image access failed",
  stage: "reference_image_access",
};

function ReferenceImageList({ images }: { images: ReferenceImage[] }) {
  const { urls, loading, retry } = usePresignedReadUrls(images, ACCESS_ERROR);
  return (
    <div className="flex flex-col gap-2">
      {images.map((image) => (
        <ChatReferenceImage
          key={image.s3_key}
          imageUrl={urls[image.s3_key]}
          loading={loading && !urls[image.s3_key]}
          caption={image.caption}
          altText={image.alt_text}
          sourceTitle={image.source_title}
          sourceUrl={image.source_url}
          onRetry={retry}
          onLoadError={() =>
            errorReporter.reportMessage("Reference image display failed", {
              level: "warning",
              tags: { feature: "indy", stage: "reference_image_display" },
            })
          }
        />
      ))}
    </div>
  );
}

export function ReferenceImagesBlock({
  block,
  fallback,
}: ChatBlockRendererProps) {
  const images = parseReferenceImages(block.data);
  if (!images) return <>{fallback}</>;
  return <ReferenceImageList images={images} />;
}
